import { type ComponentProps, type ElementType, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

type TypographyProps<
  TElement extends ElementType = 'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'p',
> = {
  as?: TElement
  className?: string
  children: ReactNode
} & ComponentProps<TElement>

export function H1({
  children,
  className,
  as: Component,
  ...rest
}: TypographyProps) {
  const props = {
    className: cn(
      'scroll-m-20 text-center text-3xl font-display font-medium tracking-tight text-balance',
      className
    ),
    ...rest,
  }
  return Component ? (
    <Component {...props}>{children}</Component>
  ) : (
    <h1 {...props}>{children}</h1>
  )
}

export function H2({
  children,
  className,
  as: Component,
  ...rest
}: TypographyProps) {
  const props = {
    className: cn(
      'scroll-m-20 text-2xl font-display font-medium tracking-tight',
      className
    ),
    ...rest,
  }
  return Component ? (
    <Component {...props}>{children}</Component>
  ) : (
    <h2 {...props}>{children}</h2>
  )
}

export function H3({
  children,
  className,
  as: Component,
  ...rest
}: TypographyProps) {
  const props = {
    className: cn(
      'scroll-m-20 text-xl font-display font-medium tracking-tight',
      className
    ),
    ...rest,
  }
  return Component ? (
    <Component {...props}>{children}</Component>
  ) : (
    <h3 {...props}>{children}</h3>
  )
}

export function H4({
  children,
  className,
  as: Component,
  ...rest
}: TypographyProps) {
  const props = {
    className: cn(
      'scroll-m-20 text-lg font-display font-medium tracking-tight',
      className
    ),
    ...rest,
  }
  return Component ? (
    <Component {...props}>{children}</Component>
  ) : (
    <h4 {...props}>{children}</h4>
  )
}

export function P({
  children,
  className,
  as: Component,
  ...rest
}: TypographyProps) {
  const props = {
    className: cn('leading-7 [&:not(:first-child)]:mt-6', className),
    ...rest,
  }
  return Component ? (
    <Component {...props}>{children}</Component>
  ) : (
    <p {...props}>{children}</p>
  )
}

export function Blockquote({
  children,
  className,
  as: Component,
  ...rest
}: TypographyProps<'blockquote'>) {
  const props = {
    className: cn('mt-6 border-l-2 pl-6 italic', className),
    ...rest,
  }
  return Component ? (
    <Component {...props}>{children}</Component>
  ) : (
    <blockquote {...props}>{children}</blockquote>
  )
}

export function InlineCode({
  children,
  className,
  as: Component,
  ...rest
}: TypographyProps) {
  const props = {
    className: cn(
      'bg-muted relative rounded px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold',
      className
    ),
    ...rest,
  }
  return Component ? (
    <Component {...props}>{children}</Component>
  ) : (
    <code {...props}>{children}</code>
  )
}

export function Lead({
  children,
  className,
  as: Component,
  ...rest
}: TypographyProps) {
  const props = {
    className: cn('text-muted-foreground text-xl', className),
    ...rest,
  }
  return Component ? (
    <Component {...props}>{children}</Component>
  ) : (
    <p {...props}>{children}</p>
  )
}

export function Large({
  children,
  className,
  as: Component,
  ...rest
}: TypographyProps) {
  const props = {
    className: cn('text-lg font-medium', className),
    ...rest,
  }
  return Component ? (
    <Component {...props}>{children}</Component>
  ) : (
    <span {...props}>{children}</span>
  )
}

export function Small({
  children,
  className,
  as: Component,
  ...rest
}: TypographyProps) {
  const props = {
    className: cn('text-sm leading-none', className),
    ...rest,
  }
  return Component ? (
    <Component {...props}>{children}</Component>
  ) : (
    <small {...props}>{children}</small>
  )
}

export function Muted({
  children,
  className,
  as: Component,
  ...rest
}: TypographyProps) {
  const props = {
    className: cn('text-muted-foreground text-sm', className),
    ...rest,
  }
  return Component ? (
    <Component {...props}>{children}</Component>
  ) : (
    <p {...props}>{children}</p>
  )
}
